import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getModuleMetadata,
  listDatasets,
  listLocations,
  previewQuery,
} from "./queryBuilderApi";
import {
  apiFilterToRow,
  buildQueryConfig,
  emptyAggregation,
  emptyFilter,
  HIDDEN_FIELD_KEYS,
  locationLabel,
} from "./helpers";

/** Field key the location scope picker drives. */
const LOCATION_SCOPE_FIELD = "locationId";

/** Rows per preview page. */
const PAGE_SIZE = 25;

const EMPTY_PREVIEW = { rows: [], pagination: null, loading: false, error: null };

/**
 * Owns the Query Builder's data and selection state: it loads the selected
 * module's metadata (race-guarded), tracks the chosen fields and filter rows,
 * and runs paginated previews. Presentational components consume this — they
 * hold no data logic of their own.
 *
 * Two effects talk to the backend: one loads the registered datasets on mount,
 * the other reloads field metadata whenever the selected dataset changes —
 * fetch-id guarded so a stale response can never overwrite a newer one.
 *
 * @param {string} initialModule module key to start on (optional).
 * @returns {object} builder state and actions.
 */
export function useQueryBuilder(initialModule = "") {
  const [module, setModule] = useState(initialModule);
  const [datasets, setDatasets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationIds, setLocationIds] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [metaLoading, setMetaLoading] = useState(false);
  const [metaError, setMetaError] = useState(null);

  const [fields, setFields] = useState([]);
  const [filters, setFilters] = useState([]);
  const [aggregations, setAggregations] = useState([]);
  const [groupBy, setGroupBy] = useState([]);
  const [preview, setPreview] = useState(EMPTY_PREVIEW);

  const fetchIdRef = useRef(0);
  const nextFilterIdRef = useRef(1);

  /** Guards preview responses so a slower earlier run can't overwrite a newer one. */
  const previewIdRef = useRef(0);

  /** A config staged by loadConfig, applied once its module's metadata loads. */
  const pendingConfigRef = useRef(null);

  /** Set by loadConfig so the preview runs once, automatically, after a load. */
  const autoPreviewRef = useRef(false);

  /** Assigns a stable _id to each editable filter row. */
  const withIds = (rows) =>
    rows.map((row) => ({ ...row, _id: nextFilterIdRef.current++ }));

  useEffect(() => {
    let active = true;
    listDatasets().then((res) => {
      if (!active) return;
      if (res.isSuccess && Array.isArray(res.data)) setDatasets(res.data);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    listLocations().then((res) => {
      if (!active) return;
      if (res.isSuccess && Array.isArray(res.data?.data)) {
        setLocations(res.data.data);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!module) {
      setMetadata(null);
      return;
    }
    const fetchId = ++fetchIdRef.current;
    setMetaLoading(true);
    setMetaError(null);
    getModuleMetadata(module).then((res) => {
      if (fetchId !== fetchIdRef.current) return;
      if (res.isSuccess && res.data) {
        setMetadata(res.data);
        const pending = pendingConfigRef.current;
        if (pending && pending.module === module) {
          setFields(pending.fields);
          setFilters(withIds(pending.filters));
          setAggregations(withIds(pending.aggregations));
          setGroupBy(pending.groupBy);
          pendingConfigRef.current = null;
        } else {
          setFields(
            res.data.fields
              .map((f) => f.key)
              .filter((key) => !HIDDEN_FIELD_KEYS.includes(key)),
          );
          setFilters([]);
          setAggregations([]);
          setGroupBy([]);
        }
        setPreview(EMPTY_PREVIEW);
      } else {
        setMetadata(null);
        setMetaError(res.message || "Failed to load module metadata");
      }
      setMetaLoading(false);
    });
  }, [module]);

  const selectModule = useCallback((next) => setModule(next), []);

  const toggleField = useCallback((key) => {
    setFields((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }, []);

  const addFilter = useCallback(() => {
    const _id = nextFilterIdRef.current;
    nextFilterIdRef.current += 1;
    setFilters((prev) => [
      ...prev,
      { ...emptyFilter(metadata?.fields?.[0]?.key), _id },
    ]);
  }, [metadata]);

  const updateFilter = useCallback((index, patch) => {
    setFilters((prev) =>
      prev.map((f, i) => (i === index ? { ...f, ...patch } : f)),
    );
  }, []);

  const removeFilter = useCallback((index) => {
    setFilters((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const addAggregation = useCallback(() => {
    const _id = nextFilterIdRef.current;
    nextFilterIdRef.current += 1;
    setAggregations((prev) => [...prev, { ...emptyAggregation(), _id }]);
  }, []);

  const updateAggregation = useCallback((index, patch) => {
    setAggregations((prev) =>
      prev.map((a, i) => (i === index ? { ...a, ...patch } : a)),
    );
  }, []);

  const removeAggregation = useCallback((index) => {
    setAggregations((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const toggleGroupBy = useCallback((key) => {
    setGroupBy((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }, []);

  /**
   * The fields shown to the user — every metadata field except the internal
   * scope-only ones (e.g. `locationId`), which the location picker drives and
   * that must never appear as a column, filter, or aggregation.
   */
  const visibleFields = useMemo(
    () =>
      (metadata?.fields ?? []).filter(
        (f) => !HIDDEN_FIELD_KEYS.includes(f.key),
      ),
    [metadata],
  );

  /** Location choices for the scope picker; the value is the id the dataset scopes on. */
  const locationOptions = useMemo(
    () =>
      locations.map((loc) => ({
        value: Number(loc.id),
        label: locationLabel(loc),
      })),
    [locations],
  );

  /**
   * Appends the location scope as a hidden `locationId in (...)` filter when one
   * or more locations are picked. Kept out of the visible filter rows: the
   * picker is its only control, so it can't be half-edited or left dangling.
   * @param {object[]} rows the user's filter rows.
   * @returns {object[]} the rows plus the scope clause, when a scope is set.
   */
  const withLocationScope = useCallback(
    (rows) =>
      locationIds.length
        ? [
            ...rows,
            {
              field: LOCATION_SCOPE_FIELD,
              operator: "in",
              value: locationIds.join(", "),
              value2: "",
            },
          ]
        : rows,
    [locationIds],
  );

  /**
   * Returns the current builder state as an API query config, or null if no
   * module metadata is loaded yet.
   * @returns {object|null} the query config ({ module, fields, filters, aggregations?, groupBy? }).
   */
  const getConfig = useCallback(() => {
    if (!metadata) return null;
    return buildQueryConfig(
      module,
      fields,
      withLocationScope(filters),
      metadata.fields,
      aggregations,
      groupBy,
    );
  }, [metadata, module, fields, filters, withLocationScope, aggregations, groupBy]);

  /**
   * Loads a saved query config into the builder for editing. If it targets a
   * different module, the config is staged until that module's metadata loads.
   * @param {object} config the saved query config ({ module, fields, filters, aggregations?, groupBy? }).
   */
  const loadConfig = useCallback(
    (config) => {
      // A load should land the user straight on the results — run the preview
      // once the config is fully applied (which is async when the dataset
      // differs and its metadata has to load first).
      autoPreviewRef.current = true;
      const allFilters = config.filters || [];
      // The location scope is stored as a hidden locationId clause; lift it back
      // into the picker and keep it out of the visible filter rows.
      const scopeClause = allFilters.find(
        (f) => f.field === LOCATION_SCOPE_FIELD,
      );
      const scopeValues = scopeClause
        ? (Array.isArray(scopeClause.value)
            ? scopeClause.value
            : [scopeClause.value]
          )
            .map(Number)
            .filter((n) => !Number.isNaN(n))
        : [];
      setLocationIds(scopeValues);
      const rows = allFilters
        .filter((f) => f.field !== LOCATION_SCOPE_FIELD)
        .map(apiFilterToRow);
      // Drop any hidden field a saved query may have selected (e.g. an older
      // query that included Start/End Date) so it never resurfaces as a column.
      const selectedFields = (config.fields || []).filter(
        (key) => !HIDDEN_FIELD_KEYS.includes(key),
      );
      const aggs = config.aggregations || [];
      const groups = config.groupBy || [];
      if (config.module === module && metadata) {
        setFields(selectedFields);
        setFilters(withIds(rows));
        setAggregations(withIds(aggs));
        setGroupBy(groups);
      } else {
        pendingConfigRef.current = {
          module: config.module,
          fields: selectedFields,
          filters: rows,
          aggregations: aggs,
          groupBy: groups,
        };
        setModule(config.module);
      }
    },
    [module, metadata],
  );

  const runPreview = useCallback(
    async (page = 1) => {
      if (!metadata) return;
      const runId = ++previewIdRef.current;
      setPreview((prev) => ({ ...prev, loading: true, error: null }));
      const cfg = buildQueryConfig(
        module,
        fields,
        withLocationScope(filters),
        metadata.fields,
        aggregations,
        groupBy,
      );
      const res = await previewQuery(cfg, page, PAGE_SIZE);
      if (runId !== previewIdRef.current) return;
      if (res.isSuccess && res.data) {
        setPreview({
          rows: res.data.data,
          pagination: res.data.pagination,
          loading: false,
          error: null,
        });
      } else {
        setPreview((prev) => ({
          ...prev,
          loading: false,
          error: res.message || "Preview failed",
        }));
      }
    },
    [metadata, module, fields, filters, withLocationScope, aggregations, groupBy],
  );

  /**
   * Runs the preview once, automatically, after a saved query is loaded — so
   * clicking Load lands the user on the results with no extra click. Waits until
   * the loaded config is applied (metadata present and fields selected), which
   * is a later tick when the dataset differs and its metadata must load first.
   */
  useEffect(() => {
    if (autoPreviewRef.current && metadata && fields.length > 0) {
      autoPreviewRef.current = false;
      runPreview(1);
    }
  }, [metadata, fields, filters, aggregations, groupBy, runPreview]);

  return {
    module,
    datasets,
    locationOptions,
    locationIds,
    setLocationIds,
    visibleFields,
    metadata,
    metaLoading,
    metaError,
    fields,
    filters,
    aggregations,
    groupBy,
    preview,
    selectModule,
    toggleField,
    addFilter,
    updateFilter,
    removeFilter,
    addAggregation,
    updateAggregation,
    removeAggregation,
    toggleGroupBy,
    runPreview,
    getConfig,
    loadConfig,
  };
}
